import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, useStripe, useElements, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { SaveCardRequest, saveCard } from 'apis/creditService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
interface CardSaveContainerProps {
  onClose: () => void;
  onSaveSuccess: (paymentMethodId: string) => void;
}

const CardSaveForm: React.FC<CardSaveContainerProps> = ({ onClose, onSaveSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [cardholderName, setCardholderName] = useState('');

  const elementOptions = {
    style: {
      base: {
        fontFamily: 'Poppins, sans-serif',
        fontSize: '14px',
        color: '#304CB2',
        '::placeholder': { color: '#A0AEC0' },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe.js has not loaded.');
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        throw new Error('One or more card elements not found.');
      }

      // Create payment method using Stripe Elements
      const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement, // Only CardNumberElement is needed; expiry and CVC are linked
        billing_details: {
          name: cardholderName,
        },
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      if (!paymentMethod?.id) {
        throw new Error('Failed to create payment method.');
      }

      // Prepare API payload (ensure no DOM elements or circular references)
      const cardData: SaveCardRequest = {
        paymentMethodId: paymentMethod.id,
      };

      // Call backend API to save the payment method
      await saveCard(cardData);

      onSaveSuccess(paymentMethod.id);
    } catch (err: any) {
      // Backend returns Stripe decline details in err.response.data; fall back to generic messages
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'An error occurred while saving the card.';
      console.error('Error in handleSubmit:', errorMessage);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
      <div className="border-b border-lightgray-100 mb-4 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-grease-100">Add a new card</h2>
          <button
            type="button"
            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition-all duration-300 hover:text-gray-600"
            onClick={onClose}
          >
            <svg
              
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
        </div>
        <p className="text-xs text-gray-400">
          Please ensure your card is enabled for online transactions.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col items-start gap-3 w-full">
        <div className="w-full">
          <label className="text-xs p-2 text-gray-400">Card Details</label>
          <CardNumberElement
            className="p-3 bg-indigo-50 w-full mt-2 border border-indigo-50 rounded-xl bg-opacity-60"
            options={{
              ...elementOptions,
              showIcon: true,
              placeholder: 'Card Number',
            }}
          />
        </div>

        <div className="w-full">
          <label className="text-xs p-2 text-gray-400">Card Holder Name</label>
          <input
            className="p-3 mt-2 bg-indigo-50 w-full border border-indigo-50 rounded-xl bg-opacity-60"
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="Enter cardholder name"
            maxLength={100}
          />
        </div>

        <div className="w-full grid lg:grid-cols-3 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="cardExpiry" className="text-xs p-2 text-gray-400">
              Expiration Date
            </label>
            <CardExpiryElement
              id="cardExpiry"
              className="p-3 mt-2 bg-indigo-50 w-full border border-indigo-50 rounded-xl bg-opacity-60"
              options={{
                ...elementOptions,
                placeholder: 'MM / YY',
              }}
            />
          </div>
          <div>
            <label htmlFor="cardCvc" className="text-xs p-2 text-gray-400">
              CVC
            </label>
            <CardCvcElement
              id="cardCvc"
              className="p-3 mt-2 bg-indigo-50 w-full border border-indigo-50 rounded-xl bg-opacity-60"
              options={elementOptions}
            />
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="w-full text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!stripe || saving}
            className="px-4 py-2 bg-[#FFD700] text-white rounded-[7px] disabled:opacity-50 transition-all duration-300 hover:bg-[#E6C200]"
          >
            {saving ? 'Saving...' : 'Save Card'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#FFD700] rounded-[7px] text-[#FFD700] transition-all duration-300 "
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const CardSaveContainer: React.FC<CardSaveContainerProps> = ({ onClose, onSaveSuccess }) => {
  return (
    <Elements stripe={stripePromise}>
      <CardSaveForm onClose={onClose} onSaveSuccess={onSaveSuccess} />
    </Elements>
  );
};

export default CardSaveContainer;